import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { deerEndpoints } from 'Deer/constants/endpoints';
import { sendRequest } from 'api/sendRequest';
import { checkNewStaticDataFeaturesEnabled, checkSandboxStaticDataFeaturesEnabled } from 'utils/helpers/featureFlags';

import {
  METRICS_REQUEST_BODY,
  METRICS_RESPONSE,
  METRIC_TYPE,
  METRIC_TYPE_TRANSFORMERS_RETURN_TYPE,
} from './types/MetricsTypes';
import { metricTypeTransformers } from './utils/overviewMetricsUtils';

const fetchMetricsAPI = async <MT extends Array<METRIC_TYPE>>({
  metrics,
  cohortId,
  employeeId,
  group,
  correlationId,
}: METRICS_REQUEST_BODY) => {
  try {
    const url = deerEndpoints.analytics.metrics;
    // @ts-expect-error send request is not typed
    const res = (await sendRequest({
      url,
      method: 'POST',
      credentials: 'include',
      body: {
        metrics,
        cohortId,
        employeeId,
        group,
        correlationId,
      },
    })) as METRICS_RESPONSE;

    const transformedData = {} as METRIC_TYPE_TRANSFORMERS_RETURN_TYPE<MT>;

    // transformation of data will be done here based on metric type

    res?.metrics?.forEach((metric) => {
      const metricType = metric.metric;
      const dataTransformer = metricTypeTransformers[metricType];
      if (dataTransformer) {
        transformedData[metric.metric] = dataTransformer(metric);
      }
    });

    return transformedData;
  } catch (error) {
    console.error('Error fetching metrics', error);
    throw new Error(error);
  }
};

export const generateMetricsQueryKey = ({
  cohortId,
  employeeId,
  metrics,
  group,
  correlationId,
}: METRICS_REQUEST_BODY) => {
  const metricsNameAnalyticsType = metrics
    ?.map(({ metricType, analyticsType, params }) => {
      if (params && params.time) {
        const { endDate, periodType, periodCount, intervalCount } = params.time;
        return `${metricType}_${analyticsType}_${endDate}_${periodType}_${periodCount}_${intervalCount}`;
      }
      return `${metricType}_${analyticsType}`;
    })
    .join('_');
  return ['metrics', cohortId, employeeId, metricsNameAnalyticsType, group, correlationId];
};

export const useGetOverviewMetrics = <MT extends Array<METRIC_TYPE>>({
  cohortId,
  employeeId,
  metrics,
  group,
  correlationId,
}: METRICS_REQUEST_BODY) => {
  // Generate a stable query key
  const queryKey = useMemo(
    () => generateMetricsQueryKey({ cohortId, employeeId, metrics, group, correlationId }),
    [cohortId, employeeId, metrics, group, correlationId]
  );

  const isFeatureEnabled = !checkSandboxStaticDataFeaturesEnabled() && !checkNewStaticDataFeaturesEnabled();
  const visierToken = metrics.find((metric) => metric.params?.token)?.params.token || '';
  const hasToken = !!visierToken && visierToken !== ''; // Check if token is available and not empty
  const placeholderData = isFeatureEnabled ? undefined : null;

  // Memoize the enabled condition
  const queryEnabled = useMemo(() => isFeatureEnabled && hasToken, [isFeatureEnabled, hasToken]);

  const fetchMetrics = useMemo(
    () => () => fetchMetricsAPI<MT>({ metrics, cohortId, employeeId, group, correlationId }),
    [metrics, cohortId, employeeId, group, correlationId]
  );

  // Use the useQuery hook
  const { data, ...rest } = useQuery<METRIC_TYPE_TRANSFORMERS_RETURN_TYPE<MT>>({
    queryKey,
    queryFn: fetchMetrics,
    enabled: queryEnabled,
    placeholderData,
  });

  return { data, ...rest };
};

export const useGetOverviewMetricsWithoutVisierToken = <MT extends Array<METRIC_TYPE>>({
  cohortId,
  employeeId,
  metrics,
  showApiData,
}: METRICS_REQUEST_BODY) => {
  // Generate a stable query key
  const queryKey = useMemo(
    () => generateMetricsQueryKey({ cohortId, employeeId, metrics }),
    [cohortId, employeeId, metrics]
  );

  const isFeatureEnabled =
    (!checkSandboxStaticDataFeaturesEnabled() && !checkNewStaticDataFeaturesEnabled()) || showApiData;
  const placeholderData = isFeatureEnabled ? undefined : null;

  const fetchMetrics = useMemo(
    () => () => fetchMetricsAPI<MT>({ metrics, cohortId, employeeId }),
    [metrics, cohortId, employeeId]
  );

  // Use the useQuery hook
  const { data, ...rest } = useQuery<METRIC_TYPE_TRANSFORMERS_RETURN_TYPE<MT>>({
    queryKey,
    queryFn: fetchMetrics,
    enabled: isFeatureEnabled,
    placeholderData,
  });

  return { data, ...rest };
};
